// services/agreementService.js
// Consolidated agreement service — handles all agreement-related API calls:
//   - Fetching master PDF URLs (GET /fetch/agreement_url)
//   - Viewing/downloading manager agreements (GET/POST /managers/settings/agreements)
//   - Admin billing overview (GET /admin/settings/billing/overview)

import api from '@/core/api/api';
import { putToPresignedUrl } from '@/services/documentService';

const agreementService = {
  // ---------------------------------------------------------------------------
  // Master PDF access (all roles)
  // ---------------------------------------------------------------------------

  /**
   * Presigned GET URL for a master agreement PDF (authenticated).
   * @param {'terms'|'revshare'|'accredited'|'subscription'|'privacy'} type
   * @returns {Promise<{ url, agreement_type, expires_in }>}
   */
  getAgreementPdfUrl: async (type = 'terms') => {
    try {
      const response = await api.get(`/fetch/agreement_url?type=${type}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${type} agreement PDF URL:`, error);
      throw new Error(
        error.response?.data?.error ||
        `Failed to load the ${type} agreement. Please try again.`
      );
    }
  },

  /**
   * Firm-specific rev-share draft PDF (authenticated managers).
   * Sync path — runs ensure_rev_share_draft inline and returns presigned draft URL.
   */
  getAgreementPersonalizedPdfUrl: async (type = 'revshare') => {
    try {
      const response = await api.get(
        `/fetch/agreement_url?type=${type}&personalized=true`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching personalized ${type} agreement PDF:`, error);
      throw new Error(
        error.response?.data?.error ||
        'Failed to load your agreement PDF. Please try again.'
      );
    }
  },

  /**
   * Firm-specific rev-share draft PDF (authenticated managers).
   * Alias for prepare-revshare-draft + draft=true polling (prefer getAgreementPersonalizedPdfUrl).
   */
  getRevShareDraftPdfUrl: async () => {
    try {
      const response = await api.get('/fetch/agreement_url?type=revshare&draft=true');
      return response.data;
    } catch (error) {
      console.error('Error fetching rev-share draft PDF URL:', error);
      throw new Error(
        error.response?.data?.error ||
        'Failed to load your agreement PDF. Please try again.'
      );
    }
  },

  /**
   * Queue background rev-share draft personalization (authenticated managers).
   */
  prepareRevShareDraft: async () => {
    try {
      const response = await api.post('/fetch/agreement_url/prepare-revshare-draft');
      return response.data;
    } catch (error) {
      console.error('Error preparing rev-share draft:', error);
      throw error;
    }
  },

  /**
   * Institution-specific accredited declaration draft PDF (authenticated investors).
   * @param {{ attestationEntityName: string, qualificationCategories: string[] }} params
   */
  getAccreditedDraftPdfUrl: async ({ attestationEntityName, qualificationCategories }) => {
    try {
      const categories = (qualificationCategories || []).join(',');
      const params = new URLSearchParams({
        type: 'accredited',
        draft: 'true',
        attestation_entity_name: attestationEntityName || '',
        qualification_categories: categories,
      });
      const response = await api.get(`/fetch/agreement_url?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching accredited draft PDF URL:', error);
      throw new Error(
        error.response?.data?.error ||
          'Failed to load your declaration PDF. Please try again.'
      );
    }
  },

  /**
   * Sync accredited draft personalization (entity name + qualification categories).
   * @param {{ attestationEntityName: string, qualificationCategories: string[] }} payload
   */
  prepareAccreditedDraft: async ({ attestationEntityName, qualificationCategories }) => {
    try {
      const response = await api.post('/fetch/agreement_url/prepare-accredited-draft', {
        attestation_entity_name: attestationEntityName || '',
        qualification_categories: qualificationCategories || [],
      });
      return response.data;
    } catch (error) {
      console.error('Error preparing accredited draft:', error);
      throw error;
    }
  },

  /**
   * Poll accredited draft after prepare — retries until presigned URL is ready.
   * @param {{ attestationEntityName: string, qualificationCategories: string[] }} params
   */
  loadAccreditedPersonalizedDraft: async ({ attestationEntityName, qualificationCategories }) => {
    await agreementService.prepareAccreditedDraft({
      attestationEntityName,
      qualificationCategories,
    });
    return agreementService.getAccreditedDraftPdfUrl({
      attestationEntityName,
      qualificationCategories,
    });
  },

  /**
   * Presigned GET URL for a master agreement PDF (no auth — registration/footer).
   * @param {'terms'|'revshare'|'accredited'|'subscription'|'privacy'} type
   */
  getPublicAgreementPdfUrl: async (type = 'terms') => {
    try {
      const response = await api.get(`/public/agreement_url?type=${type}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching public ${type} agreement PDF URL:`, error);
      throw new Error(
        error.response?.data?.error ||
        `Failed to load the ${type} agreement. Please try again.`
      );
    }
  },

  // ---------------------------------------------------------------------------
  // Manager-facing agreement management (GET /managers/settings/agreements)
  // ---------------------------------------------------------------------------

  /**
   * Get active agreements (T&C and rev-share) for the current manager, including
   * S3 presigned download URLs for signed copies when available.
   * @returns {Promise<{ terms_and_conditions, rev_share_agreement }>}
   */
  getAgreements: async () => {
    const response = await api.get('/managers/settings/agreements');
    return response.data;
  },

  /**
   * Request a download link for a specific signed agreement.
   * Returns either an S3 presigned URL (if a signed copy exists) or falls back
   * to the master PDF URL.
   * @param {'terms'|'revshare'} agreementType
   * @param {string|null} agreementId
   * @returns {Promise<{ url, source, type }>}
   */
  downloadAgreement: async (agreementType, agreementId = null) => {
    const response = await api.post('/managers/settings/agreements/download', {
      agreement_type: agreementType,
      agreement_id: agreementId
    });
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Investor-facing agreement management (GET /investors/settings/agreements)
  // ---------------------------------------------------------------------------

  /**
   * Get T&C and accredited declaration for the current investor.
   * @returns {Promise<{ terms_and_conditions, accredited_investor_declaration }>}
   */
  getInvestorAgreements: async () => {
    const response = await api.get('/investors/settings/agreements');
    return response.data;
  },

  /**
   * Request a download link for investor T&C or accredited declaration.
   * @param {'terms'|'accredited'} agreementType
   * @returns {Promise<{ url, source, type }>}
   */
  downloadInvestorAgreement: async (agreementType) => {
    const response = await api.post('/investors/settings/agreements/download', {
      agreement_type: agreementType,
    });
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Direct S3 PDF upload (after signing)
  // ---------------------------------------------------------------------------

  /**
   * Upload a rendered signed-agreement PDF blob directly to S3 via a presigned
   * PUT URL (fire-and-forget — non-fatal if it fails).
   * @param {string} uploadUrl  Presigned S3 PUT URL
   * @param {Blob}   pdfBlob    PDF blob from @react-pdf/renderer
   * @returns {Promise<boolean>} true on success
   */
  uploadSignedPdf: async (uploadUrl, pdfBlob) => {
    try {
      await putToPresignedUrl(uploadUrl, pdfBlob, 'application/pdf');
      return true;
    } catch (err) {
      console.warn('Signed PDF upload failed (non-fatal):', err);
      return false;
    }
  },

  // ---------------------------------------------------------------------------
  // Admin endpoints
  // ---------------------------------------------------------------------------
  admin: {
    /**
     * Get billing overview — all firms grouped by billing status.
     * @returns {Promise<Object>}
     */
    getBillingOverview: async () => {
      const response = await api.get('/admin/settings/billing/overview');
      return response.data;
    },

    /**
     * List all uploaded versions of every agreement type, plus the currently
     * active version for each (read from the CONFIG_TABLE DynamoDB table for the environment).
     * @returns {Promise<{
     *   terms_and_conditions: { display_name, active_version, versions[] },
     *   revenue_share_agreement: { ... },
     *   subscription_agreement: { ... },
     *   accredited_investor_undertaking: { ... }
     * }>}
     */
    getAgreementDocuments: async () => {
      const response = await api.get('/admin/settings/agreements/documents');
      return response.data;
    },

    /**
     * Get a presigned S3 PUT URL to upload a new agreement PDF version.
     * Returns 409 if that version already exists.
     * @param {string} agreementType - 'terms_and_conditions' | 'revenue_share_agreement' | 'subscription_agreement' | 'accredited_investor_undertaking'
     * @param {string} version       - e.g. '1.1'
     * @returns {Promise<{ upload_url, s3_key, expires_in }>}
     */
    getAgreementUploadUrl: async (agreementType, version) => {
      const response = await api.post('/admin/settings/agreements/documents/upload-url', {
        agreement_type: agreementType,
        version
      });
      return response.data;
    },

    /**
     * Upload a PDF blob directly to S3 using a presigned PUT URL.
     * Called after getAgreementUploadUrl().
     * @param {string} uploadUrl - Presigned S3 PUT URL
     * @param {File}   file      - File object from the file input
     * @returns {Promise<boolean>} true on success
     */
    uploadAgreementFile: async (uploadUrl, file) => {
      await putToPresignedUrl(uploadUrl, file, 'application/pdf');
      return true;
    },

    /**
     * Activate a specific version for an agreement type.
     * Writes to CONFIG_TABLE (e.g. dev-hfrc-config in dev) so all backend functions serve the new version.
     * @param {string} agreementType
     * @param {string} version
     * @param {string} activatedBy - admin email / identifier
     * @returns {Promise<{ message, agreement_type, activation }>}
     */
    activateAgreementVersion: async (agreementType, version, activatedBy) => {
      const response = await api.post('/admin/settings/agreements/documents/activate', {
        agreement_type: agreementType,
        version,
        activated_by: activatedBy
      });
      return response.data;
    },

    /**
     * Fetch all rev-share agreements (with HFR countersignature status) for admin view.
     * @returns {Promise<{ agreements: Array }>}
     */
    getRevShareAgreements: async () => {
      const response = await api.get('/admin/settings/agreements/dual-signature');
      return response.data;
    },

    /**
     * Request HFR countersignature for a specific rev-share agreement.
     * Admin-authenticated.
     * @param {string} agreementId
     * @param {string} [approverEmail] - overrides config default when provided
     * @returns {Promise<{ message, sent_to, expires_at, signing_url }>}
     */
    requestHfrCountersignature: async (agreementId, approverEmail = null) => {
      const body = {};
      if (approverEmail) body.approver_email = approverEmail;
      const response = await api.post(
        `/admin/settings/agreements/dual-signature/${agreementId}/request-hfr-signature`,
        body
      );
      return response.data;
    },

    /**
     * Fetch all dual-signature agreements (rev-share + subscription) for admin view.
     * Accepts optional ?agreement_type=rev_share|subscription and ?status= query params.
     * @returns {Promise<{ agreements: Array, total: number }>}
     */
    getAllAgreements: async ({ agreementType, status } = {}) => {
      const params = {};
      if (agreementType) params.agreement_type = agreementType;
      if (status) params.status = status;
      const response = await api.get('/admin/settings/agreements/dual-signature', { params });
      return response.data;
    },

    /**
     * Request HFR countersignature for a subscription agreement (identified by firm_id).
     * Uses the same unified endpoint as rev-share; agreement_type in body selects the branch.
     * @param {string} firmId
     * @param {string} [approverEmail]
     * @returns {Promise<{ message, firm_id, approver_email, expires_at }>}
     */
    requestHfrSubscriptionCountersignature: async (firmId, approverEmail = null) => {
      const body = { agreement_type: 'subscription' };
      if (approverEmail) body.approver_email = approverEmail;
      const response = await api.post(
        `/admin/settings/agreements/dual-signature/${firmId}/request-hfr-signature`,
        body
      );
      return response.data;
    },
  },

  // ---------------------------------------------------------------------------
  // Public — HFR countersignature signing page (no auth required)
  // ---------------------------------------------------------------------------

  /**
   * Validate a one-time signing token and retrieve rev-share agreement data for display.
   * Called from RevShareSigningPage on mount.
   * @param {string} agreementId
   * @param {string} token
   * @returns {Promise<{ firm_name, firm_address, agreement_pdf_url, agreement_version, advisor_signature, expires_at }>}
   */
  getHfrSigningData: async (agreementId, token) => {
    const response = await api.get('/admin/settings/agreements/sign/rev-share', {
      params: { id: agreementId, token },
    });
    return response.data;
  },

  /**
   * Submit HFR's countersignature on the rev-share public signing page.
   * @param {{ agreement_id, token, name, title }} payload
   * @returns {Promise<{ success, message, pdf_upload_url, pdf_s3_key }>}
   */
  submitHfrSignature: async (payload) => {
    const response = await api.post('/admin/settings/agreements/sign/rev-share', payload);
    return response.data;
  },

  /**
   * Validate a one-time signing token and retrieve subscription agreement data for display.
   * Called from SubscriptionSigningPage on mount.
   * Uses the unified rev-share signing endpoint with agreement_type=subscription.
   * @param {string} firmId
   * @param {string} token
   * @returns {Promise<{ firm_id, firm_name, agreement_pdf_url, agreement_version, advisor_signature, advisor_signed_at, expires_at }>}
   */
  getHfrSubscriptionSigningData: async (firmId, token) => {
    const response = await api.get('/admin/settings/agreements/sign/rev-share', {
      params: { id: firmId, token, agreement_type: 'subscription' },
    });
    return response.data;
  },

  /**
   * Submit HFR's countersignature on the subscription public signing page.
   * Uses the unified rev-share signing endpoint with agreement_type=subscription.
   * @param {{ firm_id, token, name, title }} payload
   * @returns {Promise<{ success, message }>}
   */
  submitHfrSubscriptionSignature: async (payload) => {
    const response = await api.post('/admin/settings/agreements/sign/rev-share', {
      ...payload,
      agreement_type: 'subscription',
    });
    return response.data;
  },
};

export default agreementService;
